import { PlusIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import usePageIndex from "../../hooks/usePageIndex";
import { useSearch } from "../../hooks/useSearch";
import { ICityPage } from "../../interfaces/endpoints/ICityPage";
import { getAllCityPages } from "../../services/CityPages.service";
import Table from "../ui/table";
import Pagination from "../ui/table/Pagination";
import Can from "../users/permissions/Can";
import CityPagesTableRow from "./CityPagesTableRow";
import CreateCityPageModal from "./CreateCityPageModal";

const PAGE_SIZE = 10;
const columns: (string | number)[] = [
  "ID",
  "Qyteti",
  "Shteti",
  "Përmbajtja",
  "Statusi",
  "Faqe Linjash",
  "", "Veprimet"
];

const CityPagesTable: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const updateSearch = (value: string) => {
    const params = new URLSearchParams(searchParams);
    value ? params.set("search", value) : params.delete("search");
    params.delete("pageindex");
    setSearchParams(params, { replace: true, state });
  };

  useEffect(() => setSearch(searchParams.get("search") || ""), [searchParams]);

  const { response, loading, handlePageChange, page, refetch } = useSearch(
    getAllCityPages,
    { otherQuery: { search, pageSize: String(PAGE_SIZE) } }
  );

  const { pageIndex } = usePageIndex() as { pageIndex: string };
  const apiData = response?.data as {
    result?: ICityPage[];
    data?: ICityPage[];
    totalCount?: number;
  } | undefined;
  const cityPages: ICityPage[] = apiData?.result ?? apiData?.data ?? [];

  return (
    <div>
      <div className="mb-2 flex w-full items-center gap-3">
        <input
          type="text"
          className="w-full max-w-sm rounded-md border px-3 py-2 text-sm"
          placeholder="Kërko sipas qytetit në tabelë..."
          value={search}
          onChange={(e) => updateSearch(e.target.value)}
        />
        <Can permissions="citypages.citypage.create">
          <button
            type="button"
            onClick={() => setCreateModalOpen(true)}
            className="flex items-center gap-2 rounded bg-primary px-3 py-2 text-sm text-white"
          >
            <PlusIcon className="h-4 w-4" /> Krijo faqe qyteti
          </button>
        </Can>
      </div>

      <CreateCityPageModal
        show={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={refetch}
      />

      <div className="flex flex-col" style={{ height: "calc(100vh - 11rem)" }}>
        <Table titles={columns} style="hover" loading={loading}>
          {cityPages.map((row) => (
            <CityPagesTableRow
              key={row.id}
              row={row}
              pageIndex={pageIndex}
              onRefetch={refetch}
            />
          ))}
        </Table>

        {/* Pagination */}
        <Pagination
          onPageChange={handlePageChange}
          totalCount={apiData?.totalCount ?? 0}
          length={apiData?.result?.length ?? cityPages.length}
          page={page}
          pageSize={PAGE_SIZE}
        />
      </div>
    </div>
  );
};

export default CityPagesTable;
